import React, { useState, useEffect, useContext } from "react";
import { Link } from "react-router-dom";
import { getprofile } from "../../../api/authentication";
import moment from "moment";
import {
  Row,
  Col,
  Card,
  Skeleton,
  Avatar,
  Typography,
  Button,
  Space,
  Divider,
} from "antd";
import { UserOutlined, MailOutlined, SettingOutlined } from "@ant-design/icons";
import { Phone } from "lucide-react";
import AppContext from "../../../context/Context";

const { Title, Text } = Typography;

const ProfileConfigration = () => {
  const [user, setUser] = useState({});
  const { config: { isDark } } = useContext(AppContext);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = await getprofile();
        if (!mounted) return;
        if (data?.code === 200) {
          setUser(data.data || {});
        } else {
          setUser({});
          console.error("getprofile error:", data?.error);
        }
      } catch (err) {
        console.error("Profile fetch error:", err);
        setUser({});
      } 
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const hasSubscription =
    user?.Terminal?.activeTradeSubscription ||
    user?.AlgoSignals?.activeTradeSubscription ||
    user?.Unlimited?.activeTradeSubscription ||
    user?.Trial?.activeTradeSubscription;

  const SubscriptionCard = ({ title, isActive, validTill }) => {
    return (
      <div className={`subscription-display-card ${isActive ? 'subscription-display-card-active' : 'subscription-display-card-expired'}`}>
        <div>
          <div className="sub-display-header">
            <Text strong className="sub-display-title">
              {title}
            </Text>
            <div className={`sub-display-status-tag ${isActive ? 'sub-display-status-tag-active' : 'sub-display-status-tag-expired'}`}>
              {isActive ? "ACTIVE" : "EXPIRED"}
            </div>
          </div>
        </div>

        <div className={`sub-display-footer ${isActive ? 'sub-display-footer-active' : 'sub-display-footer-expired'}`}>
           <Text type="secondary" style={{ fontSize: 12, fontFamily: 'monospace' }}>
              {isActive && validTill ? `Valid Until: ${moment(validTill).format("DD-MMM-YYYY")}` : isActive ? "Valid Until: —" : (title === "Trial" ? "Status: Trial period ended" : "Status: Subscription has ended")}
           </Text>
        </div>
      </div>
    );
  };

  return (
    <Row justify="center" className="profile-container-row">
      <Col xs={24} sm={22} md={20} lg={16} xl={14}>
        <Card bordered={false} className="profile-card-main">
            {/* Header Section */}
            <Row justify="space-between" style={{alignItems: "center"}}>
              <Col flex="auto">
                <div style={{ display: "flex", alignItems: "center", gap: 24, flexWrap: "wrap" }}>
                  <div style={{ position: "relative" }}>
                    <div className="profile-avatar-box">
                         <UserOutlined style={{ fontSize: 40, color: "#fff" }} />
                    </div>
                  </div>

                  <Space direction="vertical" size={2}>
                    <Title level={3} className="profile-header-name">
                      {user.firstName || "User"} {user.lastName || ""}
                    </Title>
                     <div style={{ marginTop: 8 }}>
                         {hasSubscription ? (
                            <div className="status-badge-custom status-badge-custom-active">
                                <span style={{ marginRight: 4 }}>●</span> ACTIVE
                            </div>
                         ) : (
                            <div className="status-badge-custom status-badge-custom-expired">
                                <span style={{ marginRight: 4 }}>●</span> EXPIRED
                            </div>
                         )}
                     </div>
                  </Space>
                </div>
              </Col>

              <Col>
                <Space wrap>
                   <Link to="/subscriptions">
                    <Button>
                        My Subscriptions
                    </Button>
                   </Link>
                </Space>
              </Col>
            </Row>

             {/* Contact Info Section */}
            <Row style={{marginTop: "30px"}} gutter={[24, 24]}>
              <Col xs={24} md={12}>
                <div className="profile-info-item-card">
                   <div className="profile-info-icon-container">
                       <MailOutlined style={{ fontSize: 24, color: "#fff" }} />
                   </div>
                   <div style={{ display: 'flex', flexDirection: 'column', width: "100%", overflow: "hidden" }}>
                       <Text className="profile-info-label-text">Email</Text>
                       <Text strong className="profile-info-value-text" style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{user.email || "—"}</Text>
                   </div>
                </div>
              </Col>
              <Col xs={24} md={12}>
                <div className="profile-info-item-card">
                   <div className="profile-info-icon-container">
                       <Phone size={24} color="#fff" />
                   </div>
                   <div style={{ display: 'flex', flexDirection: 'column' }}>
                       <Text className="profile-info-label-text">Mobile</Text>
                       <Text strong className="profile-info-value-text">{user.mobile || "—"}</Text>
                   </div>
                </div>
              </Col>
            </Row>

            <div className="profile-title-bar-container">
                <div className="profile-title-indicator"></div>
                <Title level={4} style={{ margin: 0, fontWeight: 600 }}>Subscription Status</Title>
            </div>

            {/* Subscriptions Grid */}
            <Row gutter={[24, 24]}>
              <Col xs={24} md={12} lg={12} xl={8}>
                <SubscriptionCard
                    title="Terminal"
                    isActive={user?.Terminal?.activeTradeSubscription}
                    validTill={user?.Terminal?.subscriptionValidTill}
                />
              </Col>
              <Col xs={24} md={12} lg={12} xl={8}>
                <SubscriptionCard
                    title="Algo Signals"
                    isActive={user?.AlgoSignals?.activeTradeSubscription}
                    validTill={user?.AlgoSignals?.subscriptionValidTill}
                />
              </Col>
              <Col xs={24} md={12} lg={12} xl={8}>
                 <SubscriptionCard
                    title="Unlimited"
                    isActive={user?.Unlimited?.activeTradeSubscription}
                    validTill={user?.Unlimited?.subscriptionValidTill}
                />
              </Col>
              <Col xs={24} md={12} lg={12} xl={8}>
                 <SubscriptionCard
                    title="Trial"
                    isActive={user?.Trial?.activeTradeSubscription}
                    validTill={user?.Trial?.subscriptionValidTill}
                />
              </Col>
            </Row>

            {/* Upgrade Banner Section */}
            <div className="profile-upgrade-banner">
              <div className="d-flex flex-column">
                <Title level={4} className="upgrade-banner-title-text">
                  Upgrade Your Experience
                </Title>
                <Text className="upgrade-banner-desc-text">
                  Unlock premium features and extend your subscription
                </Text>
              </div>
              <Link to="/pricing">
                <Button type="primary">
                  Purchase Plan
                </Button>
              </Link>
            </div>
        </Card>
      </Col>
    </Row>
  );
};

export default ProfileConfigration;
